import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { ArenaPageQuery } from "arena/queries";
import { useArena } from "core/components/ArenaProvider";
import {
  isChannelProxyPage,
  buildProxyPageOverride,
} from "arena/components/ProxyPage/proxyPageOverride";

/**
 * Первые сегменты адресов, у которых есть свой маршрут: там прокси-страницы
 * быть не может, и запрос не нужен. Остальные пути обрабатывает Page.
 */
const ROUTED = new Set([
  "",
  "video",
  "shorty",
  "stream",
  "tv",
  "channel",
  "post",
  "digitalAsset",
  "book",
  "product",
  "tag",
  "collection",
  "favorites",
  "contents",
  "search",
  "history",
  "reads",
  "invalidOauth",
  "unsubscribe",
  "disableSubscription",
  "invalidToken",
  "purchases",
  "document",
]);

/**
 * Прокси-страница канала по текущему адресу.
 *
 * Канал на прокси-странице показывается тем же RouteDialog, что и
 * /tv/:slug, поэтому AppRoutes нужно знать о ней до рендера диалога.
 * Запрос тот же, что делает Page, Apollo объединяет их в один.
 *
 * Возвращает null или { page, match, override }: match подставляется в
 * RouteDialog как virtualMatch, override в ProxyPageOverrideContext.
 */
export default function useProxyChannel(location) {
  const arena = useArena();
  const first = location.pathname.split("/")[1] || "";
  const skip = ROUTED.has(first);

  const { data } = useQuery(ArenaPageQuery, {
    variables: { path: location.pathname },
    skip,
  });

  const page = skip ? null : data?.arenaProxyPage;

  return useMemo(() => {
    if (!isChannelProxyPage(page)) return null;

    return {
      page,
      match: {
        params: { slug: page.content.channel.slug },
        pathname: location.pathname,
        pathnameBase: location.pathname,
        pattern: { path: "/tv/:slug", caseSensitive: false, end: true },
      },
      override: buildProxyPageOverride(page, arena),
    };
  }, [page, location.pathname, arena]);
}
